'use client'

import { useMemo } from 'react'
import { ActionBarProgressBar } from './action-bar-progress-bar'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useCredits } from '@/features/subscription/credits/use-credits'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'
import { useIsSuperUser } from '@/utils/clerk/use-is-super-user'
import { useSettingsModal } from '@/features/settings-modal/settings-modal-context'
import { useSidebarModalStore } from '@/features/modal/sidebar-modal-store'
import { getUpsellCtaLabel, useSubscriptionTier } from '@/features/subscription/hooks/use-subscription-tier'
import { useIsMockedFreeTest } from '@/features/subscription/billing/dev/billing-mock-state'

interface ActionBarDropdownCreditsProps {
    onClose?: () => void
}

export const ActionBarDropdownCredits = ({ onClose }: ActionBarDropdownCreditsProps) => {
    const { openSettingsModal } = useSettingsModal()
    const credits = useCredits()
    const openSidebarModal = useSidebarModalStore((s) => s.open)

    const hasMultipleCreditTypes = useMemo(() => {
        if (!credits) return false
        const creditTypes = new Set(credits.creditSegments.map((segment) => segment.label))
        return creditTypes.size > 1
    }, [credits])

    const isSuperUser = useIsSuperUser()
    const { tier } = useSubscriptionTier()
    // The FREE_TEST billing mock previews the upgrade CTA as a super user.
    const isMockedFreeTest = useIsMockedFreeTest()
    const showFreeTestUpgradeCta = tier === 'free_test' && !(isSuperUser && !isMockedFreeTest)

    if (!credits) return null

    const activeCreditType = credits.activeCreditType

    return (
        <TooltipProvider>
            <div className="w-full flex flex-col px-4 py-4 bg-[#1a1a1a] rounded-lg">
                <div className="flex justify-between items-center mb-2">
                    <span className="text-[#a3a3a3] text-[11px]">
                        {credits.totalAvailable.formattedCredits} credits left
                    </span>
                    <div className="flex items-center gap-2">
                        {isSuperUser && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openSidebarModal('credit-top-up')}
                                className="h-auto p-1 text-[11px] text-green-400 hover:text-green-300 hover:bg-green-400/10"
                            >
                                <Plus className="h-3 w-3" />
                            </Button>
                        )}
                        {!showFreeTestUpgradeCta && (
                            <button
                                style={{
                                    background: 'linear-gradient(267.38deg, #FFA248 -7.86%, #EF5B5B 116.12%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                }}
                                onClick={() => {
                                    onClose?.()
                                    openSettingsModal({ activeTab: 'plans', plansSource: 'credits_widget' })
                                }}
                                className="bg-clip-text text-transparent text-[11px] font-medium opacity-100 hover:opacity-75 transition-all duration-200 cursor-pointer"
                            >
                                Manage
                            </button>
                        )}
                    </div>
                </div>

                <div className="mb-3">
                    <ActionBarProgressBar segments={credits.creditSegments} className="rounded-full" />
                </div>

                {activeCreditType && hasMultipleCreditTypes && (
                    <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: activeCreditType.color }} />
                        <span className="text-[#a3a3a3] text-[11px]">{activeCreditType.label} consumed first</span>
                    </div>
                )}

                <button
                    onClick={() => {
                        onClose?.()
                        openSidebarModal('credits-info')
                    }}
                    className="text-[#a3a3a3] text-[11px] hover:text-white/80 transition-colors mt-2 text-left cursor-pointer underline underline-offset-2 decoration-[#a3a3a3]/40"
                >
                    Learn about Credits
                </button>

                {showFreeTestUpgradeCta && (
                    <button
                        onClick={() => {
                            onClose?.()
                            openSettingsModal({ activeTab: 'plans', plansSource: 'credits_widget' })
                        }}
                        style={{
                            background: 'linear-gradient(267.38deg, #FFA248 -7.86%, #EF5B5B 116.12%)',
                        }}
                        className="mt-3 w-full rounded-md py-1.5 px-3 text-[12px] font-medium text-white hover:brightness-110 transition-all duration-200 cursor-pointer"
                    >
                        {getUpsellCtaLabel(tier)}
                    </button>
                )}
            </div>
        </TooltipProvider>
    )
}
